import { useState, useEffect } from 'react'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { Input } from '../components/ui/Input'
import { Select } from '../components/ui/Select'
import { Button } from '../components/ui/Button'
import type { Order, Gift, YearGroup } from '../lib/types'
import { RECIPIENT_TYPE_LABELS, YEAR_GROUP_LABELS } from '../lib/types'

export default function GiftPickerPage() {
  const [orders, setOrders] = useState<Order[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedYearGroup, setSelectedYearGroup] = useState<string>('all')
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [toggleError, setToggleError] = useState<string | null>(null)
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    loadGifts()
  }, [])

  const loadGifts = async () => {
    setIsLoading(true)
    setLoadError(false)
    try {
      const ordersData = await api.getGiftPickerOrders()
      setOrders(ordersData)
    } catch (error) {
      console.error('Failed to load gifts:', error)
      setLoadError(true)
    } finally {
      setIsLoading(false)
    }
  }

  // Flatten all gifts from all orders with order numbers
  const allGifts: (Gift & { orderName: string; orderNumber: string })[] = orders.flatMap((order) =>
    (order.gifts || []).map(gift => ({
      ...gift,
      orderName: order.fullName,
      orderNumber: order.orderNumber ? String(order.orderNumber).padStart(3, '0') : '---', // Use database orderNumber
    }))
  )

  // Apply filters and sort
  const filteredGifts = allGifts
    .filter(gift => {
      const matchesSearch = gift.fromName.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesYearGroup = selectedYearGroup === 'all' || gift.yearGroup === selectedYearGroup
      const matchesStatus =
        selectedStatus === 'all' ||
        (selectedStatus === 'picked' && gift.isPicked) ||
        (selectedStatus === 'unpicked' && !gift.isPicked)
      return matchesSearch && matchesYearGroup && matchesStatus
    })
    .sort((a, b) => a.fromName.localeCompare(b.fromName))

  // Calculate statistics
  const totalGifts = allGifts.length
  const pickedGifts = allGifts.filter(g => g.isPicked).length
  const remainingGifts = totalGifts - pickedGifts

  const giftsByYearGroup = allGifts.reduce((acc, gift) => {
    if (!acc[gift.yearGroup]) {
      acc[gift.yearGroup] = { total: 0, picked: 0 }
    }
    acc[gift.yearGroup].total += 1
    if (gift.isPicked) {
      acc[gift.yearGroup].picked += 1
    }
    return acc
  }, {} as Record<string, { total: number; picked: number }>)

  const handleTogglePicked = async (gift: Gift) => {
    setToggleError(null)
    setPendingIds(prev => new Set(prev).add(gift.id))
    try {
      const updatedGift = await api.markGiftPicked(gift.id, !gift.isPicked)

      // Update the gift in the orders state
      setOrders(prevOrders =>
        prevOrders.map(order => ({
          ...order,
          gifts: order.gifts?.map(g => (g.id === gift.id ? updatedGift : g)),
        }))
      )

      // Show success message
      setSuccessMessage(updatedGift.isPicked ? 'Gift marked as picked' : 'Gift marked as unpicked')
      setTimeout(() => setSuccessMessage(null), 2000)
    } catch (error) {
      console.error('Failed to update gift:', error)
      setToggleError(`Could not update the gift from ${gift.fromName}. It has not been saved; please try again.`)
    } finally {
      setPendingIds(prev => {
        const next = new Set(prev)
        next.delete(gift.id)
        return next
      })
    }
  }

  const handleExportCSV = () => {
    // Create CSV header
    const headers = [
      'Order Number',
      'Child Name',
      'Parent Name',
      'Gift For',
      'Category',
      'Year Group',
      'Picked'
    ]

    // Create CSV rows
    const rows = allGifts.map(gift => [
      gift.orderNumber,
      gift.fromName,
      gift.orderName,
      gift.recipientName,
      RECIPIENT_TYPE_LABELS[gift.recipientType],
      YEAR_GROUP_LABELS[gift.yearGroup],
      gift.isPicked ? 'YES' : 'NO'
    ])

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `gift-picker-${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Loading gifts...</p>
      </div>
    )
  }

  if (loadError) {
    return (
      <div role="alert" className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex flex-wrap items-center gap-3">
        <span>Could not load gifts. Check your connection and try again.</span>
        <button onClick={() => loadGifts()} className="min-h-[44px] px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-medium">
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Gift Picker</h1>
        <Button onClick={handleExportCSV} variant="outline">
          <svg aria-hidden="true" className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export CSV
        </Button>
      </div>

      {toggleError && (
        <div role="alert" className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {toggleError}
        </div>
      )}

      {/* Success toast */}
      <div role="status" className={successMessage ? 'fixed top-4 right-4 bg-green-700 text-white px-4 py-2 rounded-lg shadow-lg z-50' : 'sr-only'}>
        {successMessage}
      </div>

      {/* Statistics */}
      <Card variant="bordered">
        <CardContent>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Overview</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <p className="text-sm text-gray-600">Total Gifts</p>
              <p className="text-2xl font-bold text-gray-900">{totalGifts}</p>
            </div>

            <div className="space-y-1">
              <p className="text-sm text-gray-600">Remaining to Pick</p>
              <p className="text-2xl font-bold text-orange-600">{remainingGifts}</p>
            </div>

            <div className="space-y-1">
              <p className="text-sm text-gray-600">Picked</p>
              <p className="text-2xl font-bold text-green-600">{pickedGifts}</p>
            </div>
          </div>

          {/* Year Group Breakdown */}
          {Object.keys(giftsByYearGroup).length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Gifts by Year Group</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {Object.entries(giftsByYearGroup)
                  .sort(([a], [b]) => {
                    const order = ['reception', 'year_1', 'year_2', 'year_3', 'year_4', 'year_5', 'year_6']
                    return order.indexOf(a) - order.indexOf(b)
                  })
                  .map(([yearGroup, { total, picked }]) => (
                    <div key={yearGroup} className="flex justify-between items-center text-sm border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
                      <span className="text-gray-600">{YEAR_GROUP_LABELS[yearGroup as YearGroup]}</span>
                      <span className={`font-bold text-lg ml-2 ${picked === total ? 'text-green-600' : 'text-gray-900'}`}>
                        {picked} / {total}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      <Card variant="bordered">
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Input
                label="Search by child's name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Enter child's name..."
              />
            </div>

            <div>
              <Select
                label="Year Group"
                value={selectedYearGroup}
                onChange={(e) => setSelectedYearGroup(e.target.value)}
                options={[
                  { value: 'all', label: 'All Year Groups' },
                  { value: 'reception', label: 'Reception' },
                  { value: 'year_1', label: 'Year 1' },
                  { value: 'year_2', label: 'Year 2' },
                  { value: 'year_3', label: 'Year 3' },
                  { value: 'year_4', label: 'Year 4' },
                  { value: 'year_5', label: 'Year 5' },
                  { value: 'year_6', label: 'Year 6' },
                ]}
              />
            </div>

            <div>
              <Select
                label="Status"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                options={[
                  { value: 'all', label: 'All Gifts' },
                  { value: 'unpicked', label: 'Unpicked Only' },
                  { value: 'picked', label: 'Picked Only' },
                ]}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gifts List */}
      <Card>
        <CardContent>
          {filteredGifts.length === 0 ? (
            <p className="text-center text-gray-600 py-8">No gifts found matching your filters.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Picked
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Child's Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Gift For
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Year Group
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredGifts.map(gift => (
                    <tr
                      key={gift.id}
                      className={gift.isPicked ? 'opacity-50 bg-gray-50' : ''}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={gift.isPicked}
                          onChange={() => handleTogglePicked(gift)}
                          disabled={pendingIds.has(gift.id)}
                          aria-label={`Picked: gift from ${gift.fromName} for ${gift.recipientName}`}
                          className="w-6 h-6 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer disabled:cursor-wait disabled:opacity-50"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {gift.orderNumber}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className={gift.isPicked ? 'line-through text-gray-500' : 'text-gray-900 font-medium'}>
                            {gift.fromName}
                          </span>
                          <span className="text-xs text-gray-500 mt-1">
                            {gift.orderName}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {gift.recipientName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {RECIPIENT_TYPE_LABELS[gift.recipientType]}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {YEAR_GROUP_LABELS[gift.yearGroup]}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
